import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

import {
  Input,
  BUTTON_THEME,
  CircularLoader,
  Button,
  Chip,
  RadioGroup,
} from '@st-operations/uikit/src/v2/index.js'

import Header from '_components/header/index.jsx'
import { useRetractSidebar, useModal } from '_hooks'

import {
  getLookalikeAudienceSeeds,
  updateLookalikeAudienceSeeds,
  searchLookalikeAudienceBrands,
} from '_services/audience'
import convertObjAttrsCamelToSnakeCase from '_lib/convertObjAttrsCamelToSnakeCase'
import { logErrorAsync } from '../../lib/utility'
import MediaMeasurementCampaignsStatusBadge from '../../components/media-measurement-campaign-status-badge'
import { getLookalikeAudienceSeedsSchema } from '_services/audience'
import MultiSelectGroupedModal from '../unified-user-profile/query-panel/multi-select-grouped-modal'
import CustomSelect from '_components/custom-select'
import Select from '_components/select'
import { ENV_OPTIONS } from '../user-id-seeds/UserIDSeedsListView'
import { createAudienceFromProcessedSeed } from '../user-id-seeds'
import Decile from './Decile'

import './styles.scss'

const ENTITY = 'lookalike-audience-seeds'

const CONFIGURATION_METHOD = {
  BRAND: 'brand',
  S3_LOCATION: 's3Location',
}
const CONFIGURATION_METHOD_OPTIONS = [
  {
    value: CONFIGURATION_METHOD.BRAND,
    label: 'Brand',
    name: 'configurationMethod',
  },
  {
    value: CONFIGURATION_METHOD.S3_LOCATION,
    label: 'S3 Location',
    name: 'configurationMethod',
  },
]

const INPUT_ID_OPTIONS = [
  {
    label: 'AFS_INDIVIDUAL_ID',
    name: 'AFS_INDIVIDUAL_ID',
  },
  {
    label: 'android_id',
    name: 'android_id',
  },
  {
    label: 'hem_md5',
    name: 'hem_md5',
  },
  {
    label: 'ip',
    name: 'ip',
  },
  {
    label: 'estid',
    name: 'estid',
  },
  {
    label: 'ios_id',
    name: 'ios_id',
  },
]

const UserIDSeed = (props) => {
  const { id } = props.match.params

  const brandsModal = useModal(false)

  const [data, setData] = useState()
  const [brandsSearchText, setBrandsSearchText] = useState('')
  const [brandSuggestions, setBrandSuggestions] = useState([])
  const [brandValuesGrouped, setBrandValuesGrouped] = useState({})

  const [loadingBrandSuggestions, setLoadingBrandSuggestions] = useState(false)
  const [inRequest, setInRequest] = useState(false)

  const [configurationMethod, setConfigurationMethod] = useState(CONFIGURATION_METHOD.BRAND)

  const disabled = ['submitted', 'processing'].includes(data?.status)

  useRetractSidebar()

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const {
          brands: { enum: brandValuesGrouped },
        } = await getLookalikeAudienceSeedsSchema()
        setBrandValuesGrouped({
          all: Object.values(brandValuesGrouped).flat(),
          ...brandValuesGrouped,
        })
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }
    fetchSchema()
  }, [])

  useEffect(() => {
    const load = async (id) => {
      try {
        const data = await getLookalikeAudienceSeeds(id)
        setData(data)
        if (data?.brands?.length) {
          setConfigurationMethod(CONFIGURATION_METHOD.BRAND)
        } else if (data?.s3Location) {
          setConfigurationMethod(CONFIGURATION_METHOD.S3_LOCATION)
        }
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    load(id)
  }, [id])

  const handleBrandSuggestionsRequest = async () => {
    try {
      setLoadingBrandSuggestions(true)
      const result = await searchLookalikeAudienceBrands({
        query: brandsSearchText,
        brands: [],
      })
      setBrandsSearchText('')
      setBrandSuggestions([...result.filter(Boolean)])
    } catch (error) {
    } finally {
      setLoadingBrandSuggestions(false)
    }
  }

  const handleBrandAdd = (newBrand) => {
    setBrandSuggestions(brandSuggestions.filter((brand) => brand.brand_id !== newBrand.brand_id))
    setData({
      ...(data || {}),
      brands: [
        ...(data.brands?.filter((brand) => brand.brand_id !== newBrand.brand_id) || []),
        newBrand,
      ],
    })
  }

  const handleBrandRemove = (brandToRemove) => {
    const newData = { ...data }
    newData.brands = data.brands.filter((brand) => brand.brand_id !== brandToRemove.brand_id)
    setData(newData)
  }

  const handleDecileUpdate = (decile) => setData({ ...data, decile })

  const handleSaveAs = async (status = 'draft') => {
    if (inRequest) return
    setInRequest('Updating')

    const newData = {
      ...data,
      status,
    }

    // clear the brands or s3Location based on the configuration method
    if (configurationMethod === CONFIGURATION_METHOD.BRAND) {
      newData.s3Location = ''
    } else if (configurationMethod === CONFIGURATION_METHOD.S3_LOCATION) {
      newData.brands = []
    }

    try {
      await updateLookalikeAudienceSeeds(id, newData)
      toast.success('Update successful!')
      handleExit()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
      setInRequest(false)
    }
  }

  const handleExit = () => {
    props.history.push(`/${ENTITY}`)
  }

  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <div className="user-id-seed">
      <Header
        title={
          <div className="flex flex-row gap-4 items-center justify-center">
            <span>User ID Seed</span>
            <MediaMeasurementCampaignsStatusBadge status={data.status} />
            <CustomSelect
              className="text-md m-0"
              value={data?.env}
              options={ENV_OPTIONS}
              onChange={({ value }) => setData({ ...data, env: value })}
              disabled={disabled}
            />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={data.status === 'draft'}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSaveAs('draft')}>
            Save as Draft
          </Button>
        </If>
        <If condition={data.status === 'processed'}>
          <Button
            theme={BUTTON_THEME.SECONDARY}
            onClick={async () => {
              const { _id } = await createAudienceFromProcessedSeed(data)
              props.history.push(`/audience-creator/${_id}`)
            }}
          >
            Create an Audience
          </Button>
        </If>
        <If condition={data.status === 'draft'}>
          <Button theme={BUTTON_THEME.PRIMARY} onClick={() => handleSaveAs('submitted')}>
            Submit
          </Button>
        </If>
      </Header>
      <div className="text-gray-700">
        <div className="flex flex-row gap-4">
          <Input
            label="Name"
            value={data.name}
            onChange={(value) => setData({ ...data, name: value })}
            disabled={disabled}
          />
          <Decile decile={data.decile} updateDecile={handleDecileUpdate} />
        </div>
        <div>
          <p>Configuration Method</p>
          <RadioGroup
            classes={{
              container: 'flex flex-row gap-8',
            }}
            options={CONFIGURATION_METHOD_OPTIONS}
            onChange={({ target: { value } }) => setConfigurationMethod(value)}
            selected={configurationMethod}
            // disabled={disabled}
          />
        </div>
        <div className="user-id-seed__configuration-method-container">
          <If condition={configurationMethod === 'brand'}>
            <p className="font-semibold text-[16px]">Filters</p>
            <div className="user-id-seed__time-window">
              <p>Time Window</p>
              <div className="user-id-seed__time-window-inputs">
                <span
                  className={classNames('font-semibold text-[14px] leading-[14px] text-gray-700', {
                    'opacity-50': disabled,
                  })}
                >
                  From
                </span>
                <Input
                  className="m-0"
                  type="date"
                  value={data.startDate?.split('T')[0] || ''}
                  onChange={(startDate) => {
                    setData({ ...data, startDate })
                  }}
                  disabled={disabled}
                />
                <span
                  className={classNames('font-semibold text-[14px] leading-[14px] text-gray-700', {
                    'opacity-50': disabled,
                  })}
                >
                  To
                </span>
                <Input
                  className="m-0"
                  type="date"
                  value={data.endDate?.split('T')[0] || ''}
                  onChange={(endDate) => {
                    setData({ ...data, endDate })
                  }}
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="flex flex-row gap-0 border-0 border-t-2 border-solid border-gray-300 mt-4">
              <div className="flex flex-col gap-0 border-0 border-r-2 border-solid border-gray-300 py-8 pr-8 w-1/2">
                <div className="flex flex-row gap-4 relative">
                  <Input
                    className="[&_input]:pr-[40px]"
                    label="Search Brands"
                    placeholder="ex. Apple, Google, Eletronics, Supermarket..."
                    value={brandsSearchText}
                    onChange={(value) => setBrandsSearchText(value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        handleBrandSuggestionsRequest()
                      }
                    }}
                    disabled={disabled}
                  />
                  <If condition={!disabled}>
                    <div
                      className="absolute top-0 right-0 cursor-pointer text-primary font-semibold"
                      onClick={brandsModal.handleOpenModal}
                    >
                      Brand List
                    </div>
                  </If>
                  <MultiSelectGroupedModal
                    title="Brands"
                    hideAllFlag
                    hideShowValue
                    hideShowOnlySelectedGroups={false}
                    open={brandsModal.isOpen}
                    onClose={brandsModal.handleCloseModal}
                    extractValue={(option) => option?.brand_id}
                    extractLabel={(option) => option?.brand}
                    extractGroupKey={(option) => option?.afs_category}
                    optionsGrouped={brandValuesGrouped}
                    selectedOptions={convertObjAttrsCamelToSnakeCase(data.brands || {}) || []}
                    onSubmit={(brands) => setData({ ...data, brands })}
                  />
                  <If condition={loadingBrandSuggestions}>
                    <CircularLoader
                      style={{ width: 28, height: 28, top: 28, position: 'absolute', right: 6 }}
                    />
                  </If>
                </div>
                <div className="flex flex-col gap-4">
                  <span
                    className={classNames(
                      'font-semibold text-[14px] leading-[14px] text-gray-700',
                      {
                        'opacity-50': disabled,
                      }
                    )}
                  >
                    Selected Brands
                  </span>
                  <Choose>
                    <When condition={!data.brands?.length}>No brands selected</When>
                    <Otherwise>
                      <div className="flex flex-wrap gap-2">
                        <For each="currentOption" index="index" of={data.brands}>
                          <div key={currentOption.brandId} title={currentOption.afs_category}>
                            <Chip
                              value={currentOption.brand}
                              index={index}
                              onClick={() => handleBrandRemove(currentOption)}
                              disabled={disabled}
                            >
                              {currentOption.brand}
                            </Chip>
                          </div>
                        </For>
                      </div>
                    </Otherwise>
                  </Choose>
                </div>
              </div>
              <div
                className={classNames('flex flex-col gap-4 py-8 pl-8 w-1/2', {
                  'opacity-50': disabled,
                })}
              >
                <span className="font-semibold text-[14px] leading-[14px] text-gray-700">
                  Suggested Brands
                </span>
                <Choose>
                  <When condition={brandSuggestions.length === 0}>
                    Search for brands in order to have suggestions
                  </When>
                  <Otherwise>
                    <div className="flex flex-wrap gap-2">
                      <For each="currentOption" index="index" of={brandSuggestions}>
                        <div key={currentOption.brandId} title={currentOption.afs_category}>
                          <Chip
                            value={currentOption.brand}
                            index={index}
                            hasAddIcon
                            onClick={() => handleBrandAdd(currentOption)}
                          />
                        </div>
                      </For>
                    </div>
                  </Otherwise>
                </Choose>
              </div>
            </div>
          </If>
          <If condition={configurationMethod === 's3Location'}>
            <Select
              className="text-md m-0 w-1/2 mb-4"
              label="Input ID"
              value={INPUT_ID_OPTIONS.find((option) => option.name === data?.inputId)}
              options={INPUT_ID_OPTIONS}
              onChange={({ name }) => setData({ ...data, inputId: name })}
              disabled={disabled}
            />
            <Input
              label="S3 Location"
              placeholder="ex. s3://sharethis/lookalikeaudienceseed/2024/11/13/seed_file_example.json"
              value={data.s3Location}
              onChange={(value) => setData({ ...data, s3Location: value })}
              disabled={disabled}
            />
          </If>
        </div>
      </div>
    </div>
  )
}

export default UserIDSeed
